import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  ReferenceLine,
  AreaChart,
  Area
} from "recharts";
import { 
  TrendingUp, 
  Calendar, 
  Target,
  AlertTriangle,
  BarChart3,
  Download
} from "lucide-react";

const RateTrendsChart = ({ historicalRates, baseline, onRefresh }) => {
  const [chartType, setChartType] = useState("line");
  const [rateType, setRateType] = useState("all");
  const [timeRange, setTimeRange] = useState("12months");
  const [showBaseline, setShowBaseline] = useState(true);

  // Process historical data for chart
  const processChartData = () => {
    if (!historicalRates || historicalRates.length === 0) return [];

    // Sort by period end date
    const sortedRates = [...historicalRates].sort((a, b) => 
      new Date(a.period_end_date) - new Date(b.period_end_date)
    );

    // Filter by time range
    let filteredRates = sortedRates;
    if (timeRange !== "all") {
      const now = new Date();
      const monthsBack = timeRange === "6months" ? 6 : timeRange === "12months" ? 12 : 24;
      const cutoffDate = new Date(now.setMonth(now.getMonth() - monthsBack));
      filteredRates = sortedRates.filter(rate => 
        new Date(rate.period_end_date) >= cutoffDate
      );
    }

    return filteredRates.map(rate => ({
      period: rate.period_name,
      period_date: new Date(rate.period_end_date).toLocaleDateString('en-US', { 
        month: 'short', 
        year: '2-digit' 
      }),
      full_date: rate.period_end_date,
      ga_rate: rate.rates?.ga_rate || 0,
      overhead_rate: rate.rates?.overhead_rate || 0,
      fringe_rate: rate.rates?.fringe_rate || 0,
      ga_pool: rate.rates?.ga_pool || 0,
      overhead_pool: rate.rates?.overhead_pool || 0,
      fringe_pool: rate.rates?.fringe_pool || 0
    }));
  };

  const chartData = processChartData();

  const getBaselineValue = (rateKey) => {
    if (!baseline || !showBaseline) return null;
    const baselineMap = {
      'ga_rate': baseline.ga_rate_target,
      'overhead_rate': baseline.overhead_rate_target,
      'fringe_rate': baseline.fringe_rate_target
    };
    return baselineMap[rateKey];
  };

  const formatTooltipValue = (value, name) => {
    if (name.includes('rate')) {
      return [`${value?.toFixed(2)}%`, name.replace('_', ' ').toUpperCase()];
    }
    return [`$${value?.toLocaleString()}`, name.replace('_', ' ').toUpperCase()];
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border rounded-lg shadow-lg">
          <p className="font-semibold text-slate-700 mb-2">{`Period: ${label}`}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {`${entry.name.replace('_', ' ').toUpperCase()}: ${
                entry.name.includes('rate') 
                  ? `${entry.value?.toFixed(2)}%` 
                  : `$${entry.value?.toLocaleString()}`
              }`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const exportChartData = () => {
    if (chartData.length === 0) {
      alert("No chart data to export");
      return;
    }

    const csvHeaders = [
      "Period",
      "Date", 
      "G&A Rate (%)",
      "Overhead Rate (%)",
      "Fringe Rate (%)",
      "G&A Pool ($)",
      "Overhead Pool ($)", 
      "Fringe Pool ($)"
    ];

    const csvData = chartData.map(row => [
      row.period,
      row.period_date,
      row.ga_rate?.toFixed(2) || "0.00",
      row.overhead_rate?.toFixed(2) || "0.00", 
      row.fringe_rate?.toFixed(2) || "0.00",
      row.ga_pool?.toLocaleString() || "0",
      row.overhead_pool?.toLocaleString() || "0",
      row.fringe_pool?.toLocaleString() || "0"
    ]);

    const csvContent = [csvHeaders, ...csvData]
      .map(row => row.map(cell => `"${cell}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Rate_Trends_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const renderRateChart = () => {
    if (chartData.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-12">
          <TrendingUp className="h-16 w-16 text-slate-300 mb-4" />
          <h3 className="text-lg font-medium text-slate-600 mb-2">No Historical Data</h3>
          <p className="text-sm text-slate-500 text-center">
            Upload multiple trial balances over time to see rate trends and variance patterns.
          </p>
        </div>
      );
    }

    const ChartComponent = chartType === "area" ? AreaChart : LineChart;

    return (
      <ResponsiveContainer width="100%" height={400}>
        <ChartComponent data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis 
            dataKey="period_date" 
            stroke="#666"
            fontSize={12}
          />
          <YAxis 
            stroke="#666"
            fontSize={12}
            tickFormatter={(value) => rateType === "pools" ? `$${value/1000}k` : `${value}%`}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend />

          {rateType === "all" && (
            <>
              {chartType === "area" ? (
                <>
                  <Area
                    type="monotone"
                    dataKey="ga_rate"
                    stackId="1"
                    stroke="#3b82f6"
                    fill="#3b82f6"
                    fillOpacity={0.6}
                    name="G&A Rate"
                  />
                  <Area
                    type="monotone"
                    dataKey="overhead_rate"
                    stackId="2"
                    stroke="#8b5cf6"
                    fill="#8b5cf6"
                    fillOpacity={0.6}
                    name="Overhead Rate"
                  />
                  <Area
                    type="monotone"
                    dataKey="fringe_rate"
                    stackId="3"
                    stroke="#10b981"
                    fill="#10b981"
                    fillOpacity={0.6}
                    name="Fringe Rate"
                  />
                </>
              ) : (
                <>
                  <Line
                    type="monotone"
                    dataKey="ga_rate"
                    stroke="#3b82f6"
                    strokeWidth={3}
                    dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
                    name="G&A Rate"
                  />
                  <Line
                    type="monotone"
                    dataKey="overhead_rate"
                    stroke="#8b5cf6"
                    strokeWidth={3}
                    dot={{ fill: '#8b5cf6', strokeWidth: 2, r: 4 }}
                    name="Overhead Rate"
                  />
                  <Line
                    type="monotone"
                    dataKey="fringe_rate"
                    stroke="#10b981"
                    strokeWidth={3}
                    dot={{ fill: '#10b981', strokeWidth: 2, r: 4 }}
                    name="Fringe Rate"
                  />
                </>
              )}
              
              {/* Baseline reference lines */}
              {showBaseline && getBaselineValue('ga_rate') && (
                <ReferenceLine y={getBaselineValue('ga_rate')} stroke="#3b82f6" strokeDasharray="5 5" />
              )}
              {showBaseline && getBaselineValue('overhead_rate') && (
                <ReferenceLine y={getBaselineValue('overhead_rate')} stroke="#8b5cf6" strokeDasharray="5 5" />
              )}
              {showBaseline && getBaselineValue('fringe_rate') && (
                <ReferenceLine y={getBaselineValue('fringe_rate')} stroke="#10b981" strokeDasharray="5 5" />
              )}
            </>
          )}

          {rateType === "ga" && (
            <>
              {chartType === "area" ? (
                <Area
                  type="monotone"
                  dataKey="ga_rate"
                  stroke="#3b82f6"
                  fill="#3b82f6"
                  fillOpacity={0.6}
                  name="G&A Rate"
                />
              ) : (
                <Line
                  type="monotone"
                  dataKey="ga_rate"
                  stroke="#3b82f6"
                  strokeWidth={3}
                  dot={{ fill: '#3b82f6', strokeWidth: 2, r: 5 }}
                  name="G&A Rate"
                />
              )}
              {showBaseline && getBaselineValue('ga_rate') && (
                <ReferenceLine 
                  y={getBaselineValue('ga_rate')} 
                  stroke="#3b82f6" 
                  strokeDasharray="5 5"
                  label={{ value: "Baseline", position: "topRight" }}
                />
              )}
            </>
          )}

          {rateType === "overhead" && (
            <>
              {chartType === "area" ? (
                <Area
                  type="monotone"
                  dataKey="overhead_rate"
                  stroke="#8b5cf6"
                  fill="#8b5cf6"
                  fillOpacity={0.6}
                  name="Overhead Rate"
                />
              ) : (
                <Line
                  type="monotone"
                  dataKey="overhead_rate"
                  stroke="#8b5cf6"
                  strokeWidth={3}
                  dot={{ fill: '#8b5cf6', strokeWidth: 2, r: 5 }}
                  name="Overhead Rate"
                />
              )}
              {showBaseline && getBaselineValue('overhead_rate') && (
                <ReferenceLine 
                  y={getBaselineValue('overhead_rate')} 
                  stroke="#8b5cf6" 
                  strokeDasharray="5 5"
                  label={{ value: "Baseline", position: "topRight" }}
                />
              )}
            </>
          )}

          {rateType === "fringe" && (
            <>
              {chartType === "area" ? (
                <Area
                  type="monotone"
                  dataKey="fringe_rate"
                  stroke="#10b981"
                  fill="#10b981"
                  fillOpacity={0.6}
                  name="Fringe Rate"
                />
              ) : (
                <Line
                  type="monotone"
                  dataKey="fringe_rate"
                  stroke="#10b981"
                  strokeWidth={3}
                  dot={{ fill: '#10b981', strokeWidth: 2, r: 5 }}
                  name="Fringe Rate"
                />
              )}
              {showBaseline && getBaselineValue('fringe_rate') && (
                <ReferenceLine 
                  y={getBaselineValue('fringe_rate')} 
                  stroke="#10b981" 
                  strokeDasharray="5 5"
                  label={{ value: "Baseline", position: "topRight" }}
                />
              )}
            </>
          )}

          {rateType === "pools" && (
            <>
              {chartType === "area" ? (
                <>
                  <Area
                    type="monotone"
                    dataKey="ga_pool"
                    stackId="1"
                    stroke="#3b82f6"
                    fill="#3b82f6"
                    fillOpacity={0.6}
                    name="G&A Pool"
                  />
                  <Area
                    type="monotone"
                    dataKey="overhead_pool"
                    stackId="2"
                    stroke="#8b5cf6"
                    fill="#8b5cf6"
                    fillOpacity={0.6}
                    name="Overhead Pool"
                  />
                  <Area
                    type="monotone"
                    dataKey="fringe_pool"
                    stackId="3"
                    stroke="#10b981"
                    fill="#10b981"
                    fillOpacity={0.6}
                    name="Fringe Pool"
                  />
                </>
              ) : (
                <>
                  <Line
                    type="monotone"
                    dataKey="ga_pool"
                    stroke="#3b82f6"
                    strokeWidth={3}
                    dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
                    name="G&A Pool"
                  />
                  <Line
                    type="monotone"
                    dataKey="overhead_pool"
                    stroke="#8b5cf6"
                    strokeWidth={3}
                    dot={{ fill: '#8b5cf6', strokeWidth: 2, r: 4 }}
                    name="Overhead Pool"
                  />
                  <Line
                    type="monotone"
                    dataKey="fringe_pool"
                    stroke="#10b981"
                    strokeWidth={3}
                    dot={{ fill: '#10b981', strokeWidth: 2, r: 4 }}
                    name="Fringe Pool"
                  />
                </>
              )}
            </>
          )}
        </ChartComponent>
      </ResponsiveContainer>
    );
  };

  return (
    <div className="space-y-6">
      {/* Chart Controls */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <BarChart3 className="h-4 w-4 text-slate-600" />
            <Select value={chartType} onValueChange={setChartType}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="line">Line Chart</SelectItem>
                <SelectItem value="area">Area Chart</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center space-x-2">
            <Target className="h-4 w-4 text-slate-600" />
            <Select value={rateType} onValueChange={setRateType}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Rates</SelectItem>
                <SelectItem value="ga">G&A Rate Only</SelectItem>
                <SelectItem value="overhead">Overhead Only</SelectItem>
                <SelectItem value="fringe">Fringe Only</SelectItem>
                <SelectItem value="pools">Pool Amounts</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center space-x-2">
            <Calendar className="h-4 w-4 text-slate-600" />
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="6months">6 Months</SelectItem>
                <SelectItem value="12months">12 Months</SelectItem>
                <SelectItem value="24months">24 Months</SelectItem>
                <SelectItem value="all">All Time</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {baseline && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowBaseline(!showBaseline)}
            >
              <Target className="h-4 w-4 mr-2" />
              {showBaseline ? "Hide" : "Show"} Baseline
            </Button>
          )}
          
          <Button
            variant="outline"
            size="sm"
            onClick={exportChartData}
            disabled={chartData.length === 0}
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* Chart Statistics */}
      {chartData.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-slate-600">Data Points</div>
              <div className="text-2xl font-bold text-blue-600">{chartData.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-slate-600">Date Range</div>
              <div className="text-sm font-medium">
                {chartData.length > 0 ? `${chartData[0].period_date} - ${chartData[chartData.length - 1].period_date}` : "N/A"}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-slate-600">Latest G&A Rate</div>
              <div className="text-lg font-bold text-blue-600">
                {chartData.length > 0 ? `${chartData[chartData.length - 1].ga_rate?.toFixed(2)}%` : "N/A"}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-slate-600">Baseline Status</div>
              <div className="flex items-center space-x-1">
                {baseline ? (
                  <>
                    <Badge variant="default" className="bg-green-100 text-green-800">Set</Badge>
                  </>
                ) : (
                  <Badge variant="outline" className="text-orange-600">Not Set</Badge>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <TrendingUp className="h-5 w-5" />
              <span>
                {rateType === "all" ? "All Rates" : 
                 rateType === "pools" ? "Pool Amounts" :
                 rateType.charAt(0).toUpperCase() + rateType.slice(1) + " Rate"} 
                Trend Analysis
              </span>
            </div>
            {baseline && showBaseline && (
              <Badge variant="outline" className="text-blue-600">
                <Target className="h-3 w-3 mr-1" />
                Baseline Shown
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {renderRateChart()}
        </CardContent>
      </Card>

      {/* Trend Insights */}
      {chartData.length >= 2 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <AlertTriangle className="h-5 w-5 text-orange-600" />
              <span>Trend Insights</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <p className="font-medium text-slate-700">G&A Rate Trend</p>
                <p className="text-slate-600">
                  {chartData[chartData.length - 1].ga_rate > chartData[0].ga_rate ? "Increasing" : "Decreasing"} over time
                  ({((chartData[chartData.length - 1].ga_rate - chartData[0].ga_rate) / chartData[0].ga_rate * 100).toFixed(1)}% change)
                </p>
              </div>
              <div>
                <p className="font-medium text-slate-700">Overhead Rate Trend</p>
                <p className="text-slate-600">
                  {chartData[chartData.length - 1].overhead_rate > chartData[0].overhead_rate ? "Increasing" : "Decreasing"} over time
                  ({((chartData[chartData.length - 1].overhead_rate - chartData[0].overhead_rate) / chartData[0].overhead_rate * 100).toFixed(1)}% change)
                </p>
              </div>
              <div>
                <p className="font-medium text-slate-700">Fringe Rate Trend</p>
                <p className="text-slate-600">
                  {chartData[chartData.length - 1].fringe_rate > chartData[0].fringe_rate ? "Increasing" : "Decreasing"} over time
                  ({((chartData[chartData.length - 1].fringe_rate - chartData[0].fringe_rate) / chartData[0].fringe_rate * 100).toFixed(1)}% change)
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default RateTrendsChart;